import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import LangEnum from '../../../lang/LangEnum';
import {
  clearUrlHash,
  extractErrorCode,
  resolveSsoCallbackErrorLang,
} from '../../../services/modules/sso/sso.callbackError.utils';
import snackbarStore from '../../../stores/Snackbar.store';
import { useTranslation } from '../../translation/TranslationContext';

const OAUTH_ERROR_CODE_PREFIX = 'api.exception.oauth.';
const OAUTH_REQUEST_EXPIRED_CODE = 'api.exception.oauth.requestExpired';

const OAUTH_ERROR_LANG_BY_CODE: Partial<Record<string, LangEnum>> = {
  'api.exception.oauth.consentWithoutAuth': LangEnum.API_EXCEPTION_OAUTH_CONSENT_WITHOUT_AUTH,
  'api.exception.oauth.invalidCredentials': LangEnum.API_EXCEPTION_OAUTH_INVALID_CREDENTIALS,
  [OAUTH_REQUEST_EXPIRED_CODE]: LangEnum.API_EXCEPTION_OAUTH_REQUEST_EXPIRED,
};

const resolveOAuthLoginErrorLang = (code: string | null): LangEnum | null => {
  if (!code) {
    return null;
  }
  if (code.startsWith(OAUTH_ERROR_CODE_PREFIX)) {
    return OAUTH_ERROR_LANG_BY_CODE[code] ?? LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN;
  }
  return resolveSsoCallbackErrorLang(code);
};

export type OAuthLoginStep =
  | { kind: 'email' }
  | { kind: 'password'; email: string }
  | { kind: 'expired'; errorMessage: string };

interface UseOAuthLoginFlowReturn {
  step: OAuthLoginStep;
  showPassword: (email: string) => void;
  showExpired: (errorMessage: string) => void;
  handleBackToEmail: () => void;
}

const useOAuthLoginFlow = (): UseOAuthLoginFlowReturn => {
  const { translate, isMessagesLoaded } = useTranslation();
  const [step, setStep] = useState<OAuthLoginStep>({ kind: 'email' });

  const errorCodeRef = useRef<string | null>(null);
  const hasReadErrorRef = useRef(false);
  const hasShownErrorRef = useRef(false);

  useLayoutEffect(() => {
    if (hasReadErrorRef.current) {
      return;
    }
    hasReadErrorRef.current = true;

    errorCodeRef.current = extractErrorCode(window.location.hash);
    clearUrlHash();
  }, []);

  useEffect(() => {
    if (!isMessagesLoaded || hasShownErrorRef.current || !errorCodeRef.current) {
      return;
    }
    hasShownErrorRef.current = true;

    if (errorCodeRef.current === OAUTH_REQUEST_EXPIRED_CODE) {
      setStep({
        kind: 'expired',
        errorMessage: translate(LangEnum.API_EXCEPTION_OAUTH_REQUEST_EXPIRED),
      });
      return;
    }

    const errorLang = resolveOAuthLoginErrorLang(errorCodeRef.current);
    if (errorLang) {
      snackbarStore.handleMessage({ type: 'error', message: translate(errorLang) });
    }
  }, [isMessagesLoaded, translate]);

  const showPassword = useCallback((email: string) => setStep({ kind: 'password', email }), []);

  const showExpired = useCallback(
    (errorMessage: string) => setStep({ kind: 'expired', errorMessage }),
    [],
  );

  const handleBackToEmail = useCallback(() => setStep({ kind: 'email' }), []);

  return { step, showPassword, showExpired, handleBackToEmail };
};

export default useOAuthLoginFlow;
